type CamelToSnake<S extends string> = S extends `${infer T}${infer U}`
	? `${T extends Capitalize<T> ? "_" : ""}${Lowercase<T>}${CamelToSnake<U>}`
	: S;

type CamelToSnakeCase<T> = T extends Array<infer U>
	? Array<CamelToSnakeCase<U>>
	: T extends object
		? {
				[K in keyof T as CamelToSnake<K & string>]: CamelToSnakeCase<T[K]>;
			}
		: T;

type SnakeToCamel<S extends string> = S extends `${infer P}_${infer Q}`
	? `${P}${Capitalize<SnakeToCamel<Q>>}`
	: S;

type SnakeToCamelCase<T> = T extends Array<infer U>
	? Array<SnakeToCamelCase<U>>
	: T extends object
		? {
				[K in keyof T as SnakeToCamel<K & string>]: SnakeToCamelCase<T[K]>;
			}
		: T;

function toSnakeCase(str: string): string {
	return str
		.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`)
		.replace(/^_/, ""); // Remove leading underscore if present
}

function keysToSnake(obj: any): any {
	if (Array.isArray(obj)) {
		return obj.map((v) => keysToSnake(v));
	} else if (obj !== null && typeof obj === "object") {
		return Object.keys(obj).reduce((result: any, key: string) => {
			const snakeKey = toSnakeCase(key);
			result[snakeKey] = keysToSnake(obj[key]);
			return result;
		}, {});
	}
	return obj;
}

export function serializeSnakeCase<T>(data: T): CamelToSnakeCase<T> {
	const snakeCased = keysToSnake(data);
	return snakeCased as CamelToSnakeCase<T>;
}

function toCamelCase(str: string): string {
	return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function keysToCamel(obj: any): any {
	if (Array.isArray(obj)) {
		return obj.map((v) => keysToCamel(v));
	} else if (obj !== null && typeof obj === "object") {
		return Object.keys(obj).reduce((result: any, key: string) => {
			const camelKey = toCamelCase(key);
			result[camelKey] = keysToCamel(obj[key]);
			return result;
		}, {});
	}
	return obj;
}

export function deserializeSnakeCase<T>(json: string): SnakeToCamelCase<T> {
	const camelCased = keysToCamel(json);
	return camelCased as SnakeToCamelCase<T>;
}
